// PaymentPage — Payment step of the booking funnel.
// Tabs: Card / UPI / Net Banking / Wallet. Travel insurance add-on.
// TODO(booking-API-PR): wire to POST /payments (Razorpay order creation).

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingProgressBar } from '@/components/BookingProgressBar';
import { Button, Input } from '@/components/primitives';
import { BookingProvider, useBooking } from '@/lib/booking-store';
import {
  PAYMENT_METHODS,
  NET_BANKING_OPTIONS,
  MOCK_FLIGHT,
  INSURANCE_PAISE,
  AIRPORT_TAXES_PAISE,
  getSeatUpgradePaise,
  formatPaise,
} from '@/lib/booking-mock';
import { cn } from '@/lib/cn';

// ─── SELECT FIELD ─────────────────────────────────────────────────────────────
function BSelect({
  label,
  value,
  onChange,
  options,
}: {
  label:    string;
  value:    string;
  onChange: (v: string) => void;
  options:  ReadonlyArray<string>;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className="text-[10px] font-bold tracking-[1.5px] uppercase text-[#8C98B0]"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
      >
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full h-12 rounded-xl px-4 bg-[#0A0F1C] text-[#F4F3F0] border border-white/[0.14] text-sm font-medium focus:outline-none focus:border-[#FF5A3C] transition-colors cursor-pointer"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', colorScheme: 'dark' }}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

// ─── CARD FORM ────────────────────────────────────────────────────────────────
interface CardFields {
  number: string;
  name:   string;
  expiry: string;
  cvv:    string;
}

function CardForm({
  card,
  setCard,
}: {
  card:    CardFields;
  setCard: (patch: Partial<CardFields>) => void;
}) {
  return (
    <div className="bg-[#131C30] rounded-[20px] border border-white/[0.07] p-7 mb-5">
      <div
        className="text-[14px] font-bold text-[#F4F3F0] mb-5"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
      >
        Card details
      </div>

      {/* Card preview */}
      <div
        className="rounded-2xl mb-6 px-6 py-5 flex flex-col justify-between"
        style={{
          height: 130,
          background: 'linear-gradient(135deg, #1B3A6B 0%, #0A0F1C 100%)',
          border: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        <div className="flex justify-between items-center">
          <span
            className="text-[12px] text-white/60 tracking-[2px] font-semibold"
            style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
          >
            GLOBAL FLYT PAY
          </span>
          {/* Mastercard orbs */}
          <div className="flex">
            <div className="w-7 h-7 rounded-full bg-[#EB001B] opacity-90" />
            <div className="w-7 h-7 rounded-full bg-[#F79E1B] opacity-90 -ml-3" />
          </div>
        </div>
        <div>
          <div
            className="text-[16px] text-white tracking-[3px] font-semibold mb-2"
            style={{ fontFamily: 'monospace' }}
          >
            {card.number || '•••• •••• •••• ••••'}
          </div>
          <div className="flex gap-6">
            <div>
              <div className="text-[9px] text-white/50 tracking-[1px]"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                CARDHOLDER
              </div>
              <div className="text-[12px] text-white font-semibold"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {card.name || 'YOUR NAME'}
              </div>
            </div>
            <div>
              <div className="text-[9px] text-white/50 tracking-[1px]"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                EXPIRES
              </div>
              <div className="text-[12px] text-white font-semibold"
                   style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
                {card.expiry || 'MM/YY'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Input
          label="Card number"
          value={card.number}
          onChange={e => setCard({ number: e.target.value })}
          placeholder="1234 5678 9012 3456"
        />
        <Input
          label="Cardholder name"
          value={card.name}
          onChange={e => setCard({ name: e.target.value })}
          placeholder="Alex Chen"
        />
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label="Expiry date"
              value={card.expiry}
              onChange={e => setCard({ expiry: e.target.value })}
              placeholder="MM/YY"
            />
          </div>
          <div className="flex-1">
            <Input
              label="CVV"
              type="password"
              value={card.cvv}
              onChange={e => setCard({ cvv: e.target.value })}
              placeholder="•••"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <div className="w-4 h-4 rounded bg-[rgba(255,90,60,0.12)] flex items-center justify-center text-[10px]">
          🔒
        </div>
        <span
          className="text-[12px] text-[#8C98B0]"
          style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
        >
          256-bit SSL secured · we never store your card details
        </span>
      </div>
    </div>
  );
}

// ─── INNER ────────────────────────────────────────────────────────────────────
function PaymentInner() {
  const navigate            = useNavigate();
  const { state, dispatch } = useBooking();
  const f                   = MOCK_FLIGHT;

  const [card, setCardState] = React.useState<CardFields>({
    number: '4242 4242 4242 4242',
    name:   'Alex Chen',
    expiry: '03/29',
    cvv:    '',
  });
  const [bank, setBank] = React.useState<string>(NET_BANKING_OPTIONS[0]);

  function setCard(patch: Partial<CardFields>) {
    setCardState(prev => ({ ...prev, ...patch }));
  }

  const selRow            = state.selectedSeat ? parseInt(state.selectedSeat, 10) : null;
  const seatUpgradePaise  = selRow ? getSeatUpgradePaise(selRow) : 0;
  const insurancePaise    = state.insurance ? INSURANCE_PAISE : 0;
  const totalPaise =
    state.selectedFare.pricePaise +
    AIRPORT_TAXES_PAISE +
    seatUpgradePaise +
    insurancePaise;

  function handleContinue() {
    navigate('/confirm');
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <BookingProgressBar step="payment" />

      <div style={{ padding: '32px clamp(16px, 4vw, 64px) 80px', maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 28 }}>

          {/* ── Left: payment forms ───────────────────────────────────────── */}
          <div>
            <h2
              className="text-[28px] font-semibold text-[#F4F3F0] mb-6 tracking-tight"
              style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif', letterSpacing: '-0.02em' }}
            >
              Payment
            </h2>

            {/* Method tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {PAYMENT_METHODS.map(m => (
                <button
                  key={m.k}
                  onClick={() => dispatch({ type: 'SET_PAYMENT', method: m.k })}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold border',
                    'transition-all duration-150 cursor-pointer',
                    state.paymentMethod === m.k
                      ? 'bg-[#FF5A3C] text-white border-[#FF5A3C]'
                      : 'bg-[#131C30] text-[#F4F3F0] border-white/[0.07] hover:border-white/[0.18]',
                  )}
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  {m.icon} {m.label}
                </button>
              ))}
            </div>

            {/* Card form */}
            {state.paymentMethod === 'card' && (
              <CardForm card={card} setCard={setCard} />
            )}

            {/* UPI */}
            {state.paymentMethod === 'upi' && (
              <div className="bg-[#131C30] rounded-[20px] border border-white/[0.07] p-7 mb-5">
                <div
                  className="text-[14px] font-bold text-[#F4F3F0] mb-4"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  UPI payment
                </div>
                <Input
                  label="Your UPI ID"
                  placeholder="yourname@okaxis"
                />
                <p
                  className="text-[12px] text-[#8C98B0] mt-3"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  You'll receive a payment request on your UPI app. Supports Google Pay, PhonePe, BHIM.
                </p>
              </div>
            )}

            {/* Net banking */}
            {state.paymentMethod === 'netbanking' && (
              <div className="bg-[#131C30] rounded-[20px] border border-white/[0.07] p-7 mb-5">
                <div
                  className="text-[14px] font-bold text-[#F4F3F0] mb-4"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  Net banking
                </div>
                <BSelect
                  label="Select your bank"
                  value={bank}
                  onChange={setBank}
                  options={[...NET_BANKING_OPTIONS]}
                />
              </div>
            )}

            {/* Wallet */}
            {state.paymentMethod === 'wallet' && (
              <div className="bg-[#131C30] rounded-[20px] border border-white/[0.07] p-7 mb-5">
                <div
                  className="text-[14px] font-bold text-[#F4F3F0] mb-4"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  Wallet
                </div>
                <div className="flex flex-col gap-3">
                  {['Paytm', 'Amazon Pay', 'Mobikwik', 'Freecharge'].map(w => (
                    <button
                      key={w}
                      className="flex items-center justify-between px-4 py-3 rounded-xl bg-[#0A0F1C] border border-white/[0.07] text-[#F4F3F0] text-[14px] font-medium hover:border-white/[0.18] transition-colors text-left"
                      style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                    >
                      {w}
                      <span className="text-[#8C98B0] text-[12px]">→</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Travel insurance */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => dispatch({ type: 'TOGGLE_INSURANCE' })}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && dispatch({ type: 'TOGGLE_INSURANCE' })}
              className={cn(
                'flex items-center gap-4 p-5 rounded-[20px] cursor-pointer border',
                'transition-all duration-150 outline-none',
                'focus-visible:ring-2 focus-visible:ring-[#FF5A3C]/60',
                state.insurance
                  ? 'border-[#FF5A3C] bg-[rgba(255,90,60,0.06)]'
                  : 'border-white/[0.07] bg-[#131C30] hover:border-white/[0.18]',
              )}
            >
              {/* Icon */}
              <div
                className="w-12 h-12 rounded-[14px] flex-shrink-0 flex items-center justify-center text-[22px]"
                style={{
                  background: state.insurance ? 'rgba(255,90,60,0.12)' : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${state.insurance ? 'rgba(255,90,60,0.3)' : 'rgba(255,255,255,0.07)'}`,
                }}
              >
                🛡️
              </div>

              {/* Copy */}
              <div className="flex-1">
                <div
                  className="text-[15px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  Travel insurance
                </div>
                <div
                  className="text-[12px] text-[#8C98B0] mt-0.5"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  Medical, trip cancellation &amp; lost luggage — up to ₹40 lakh cover
                </div>
              </div>

              {/* Price */}
              <div className="text-right flex-shrink-0">
                <div
                  className="text-[16px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                >
                  +{formatPaise(INSURANCE_PAISE)}
                </div>
                <div
                  className="text-[11px] text-[#8C98B0]"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  per person
                </div>
              </div>

              {/* Checkbox */}
              <div
                className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[12px] font-bold transition-all duration-150"
                style={{
                  background: state.insurance ? '#FF5A3C' : 'transparent',
                  border: `2px solid ${state.insurance ? '#FF5A3C' : 'rgba(255,255,255,0.14)'}`,
                }}
              >
                {state.insurance ? '✓' : ''}
              </div>
            </div>
          </div>

          {/* ── Right sidebar ─────────────────────────────────────────────── */}
          <div className="sticky top-[64px] self-start flex flex-col gap-3">
            {/* Order summary */}
            <div className="bg-[#131C30] rounded-[24px] border border-white/[0.07] p-6">
              <div
                className="text-[11px] font-semibold tracking-[1.5px] text-[#8C98B0] mb-4"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                ORDER SUMMARY
              </div>

              <div className="pb-4 border-b border-white/[0.07]">
                <div
                  className="text-[14px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  {f.airline} · {state.selectedFare.label}
                </div>
                <div
                  className="text-[12px] text-[#8C98B0] mt-1"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  {f.from} → {f.to} · Tue Mar 17 · {f.flightNo}
                </div>
                <div
                  className="text-[12px] text-[#8C98B0]"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  Alex Chen · 1 adult{state.selectedSeat ? ` · seat ${state.selectedSeat}` : ''}
                </div>
              </div>

              {/* Line items */}
              <div className="py-4 border-b border-white/[0.07] flex flex-col gap-1">
                {[
                  { l: 'Base fare',          v: formatPaise(state.selectedFare.pricePaise) },
                  { l: 'Airport taxes',       v: formatPaise(AIRPORT_TAXES_PAISE) },
                  { l: 'Platform fee',        v: formatPaise(0) },
                  ...(seatUpgradePaise > 0
                    ? [{ l: 'Seat upgrade', v: `+${formatPaise(seatUpgradePaise)}` }]
                    : []),
                  ...(state.insurance
                    ? [{ l: 'Travel insurance', v: `+${formatPaise(INSURANCE_PAISE)}` }]
                    : []),
                ].map(({ l, v }) => (
                  <div key={l} className="flex justify-between py-0.5">
                    <span
                      className="text-[13px] text-[#8C98B0]"
                      style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                    >
                      {l}
                    </span>
                    <span
                      className="text-[13px] font-semibold text-[#F4F3F0]"
                      style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                    >
                      {v}
                    </span>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex justify-between pt-4 mb-5">
                <span
                  className="text-[22px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                >
                  Total
                </span>
                <span
                  className="text-[22px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                >
                  {formatPaise(totalPaise)}
                </span>
              </div>

              {/* Pay CTA */}
              <Button
                variant="flame"
                size="lg"
                full
                onClick={handleContinue}
                className="rounded-[14px] font-bold"
              >
                Pay {formatPaise(totalPaise)} →
              </Button>

              <div
                className="text-[11px] text-[#8C98B0] text-center mt-3"
                style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
              >
                🔒 Secured by 256-bit encryption · Razorpay
              </div>
            </div>

            {/* Fine print */}
            <div
              className="px-4 py-3 rounded-[14px] bg-[#131C30] border border-white/[0.07] text-[12px] text-[#8C98B0] leading-relaxed"
              style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
            >
              By paying you agree to our{' '}
              <span className="text-[#F4F3F0]">Terms of Service</span> and{' '}
              <span className="text-[#F4F3F0]">Fare Rules</span>.
              Prices include all applicable taxes.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EXPORT ──────────────────────────────────────────────────────────────────
export function PaymentPage() {
  return (
    <BookingProvider>
      <PaymentInner />
    </BookingProvider>
  );
}
